import type { ProofChainStep } from "../../../derive-studio-actions.js";
import type { StudioApiClient } from "../../../api.js";
import { GuidedConfirmationQueueView } from "./GuidedConfirmationQueueView.js";
import { GuidedButton, GuidedFlowCopy } from "../primitives/GuidedPrimitives.js";

export function GuidedMoreAdvancedView({
  api,
  confirmationCount = 0,
  proofChainSteps = [],
  devToolsEnabled = false,
  onWipeDevProjects,
  onConfirmationQueueChanged,
}: {
  readonly api: StudioApiClient;
  readonly confirmationCount?: number;
  readonly proofChainSteps?: readonly ProofChainStep[];
  readonly devToolsEnabled?: boolean;
  readonly onWipeDevProjects?: () => void;
  readonly onConfirmationQueueChanged?: () => void;
}) {
  return (
    <section className="enrein-guided-view is-active" id="view-more" aria-label="More">
      <GuidedFlowCopy title="More" kicker="Advanced">
        <p className="enrein-guided-muted">Power tools stay here — collapsed until you need them.</p>
      </GuidedFlowCopy>

      {devToolsEnabled ? (
        <div className="enrein-guided-dev-tools">
          <p className="enrein-guided-kicker">Local testing</p>
          <p className="enrein-guided-muted">
            Reset Studio to zero projects on this dev agency. Saved client links in this browser are cleared too.
          </p>
          <GuidedButton variant="line" onClick={onWipeDevProjects}>
            Wipe all test projects
          </GuidedButton>
        </div>
      ) : null}

      <details className="enrein-guided-advanced-block">
        <summary>Launch Proof records</summary>
        {proofChainSteps.length === 0 ? (
          <p className="enrein-guided-muted">Open a project to see its proof chain snapshot here.</p>
        ) : (
          <ol className="enrein-guided-proof-chain">
            {proofChainSteps.map((step) => (
              <li key={step.id} className={`enrein-guided-proof-step enrein-guided-proof-step--${step.state}`}>
                <strong>{step.label}</strong>
                <span>{step.status}</span>
                <p className="enrein-guided-muted">{step.detail}</p>
              </li>
            ))}
          </ol>
        )}
      </details>

      <details className="enrein-guided-advanced-block">
        <summary>Batch URL audit</summary>
        <div className="enrein-guided-embedded-panel">
          <p className="enrein-guided-muted">
            Use V10 Studio → Scans / Proof for batch URL audit. Guided rollback shell does not host this flow.
          </p>
        </div>
      </details>

      <details className="enrein-guided-advanced-block" open={confirmationCount > 0}>
        <summary>Confirmation queue {confirmationCount > 0 ? `(${confirmationCount})` : ""}</summary>
        <GuidedConfirmationQueueView api={api} onQueueChanged={onConfirmationQueueChanged} />
      </details>
    </section>
  );
}
